/**
 * The Ask layer's BROWSER adapter — the whole Ask layer with no backend. HBH's API is C#, so this
 * is the adoption the user-guide-creator skill recommends: the compiled catalog
 * (`src/ask/core/catalog.data.ts`) ships in the bundle, retrieval runs in the page, the question a
 * user types never leaves the browser. `ask-api.ts` re-exports the two functions below, which have
 * the signatures MGA's HTTP client has, so `HelpAsk` does not know the difference.
 *
 * Roles come from the client's auth state (`setAskPrincipal`, called by `AppHeader`). That is
 * fine: roles only reorder results and never hide an entry.
 */
import { handleFeedback, handleSearch } from '../ask/core/handlers';
import type {
  AssistantFeedback,
  AssistantFeedbackResponse,
  AssistantSearchRequest,
  AssistantSearchResponse,
  Principal,
  TenantId,
} from '../ask/core/types';
import type { ValidationErrorBody } from '../ask/core/handlers';
import { setTelemetrySink } from '../ask/core/interaction-log';

/**
 * Where the two product events (the miss log and the feedback) go. They stay in the browser: a
 * typed question is free text and can name a person, so it is not sent to Application Insights
 * or any other service until that is a decision somebody has made. Development builds print the
 * events to the console, which is enough to tune the catalog locally; production drops them.
 */
export { setTelemetrySink, type TelemetrySink } from '../ask/core/interaction-log';

if (process.env.NODE_ENV !== 'development') setTelemetrySink({ track: () => undefined });

let principal: Principal | null = null;
let tenantId: TenantId = null;

/**
 * Called by `AppHeader` with the viewer's page-guide tiers. No user id is passed: in a browser the
 * keystroke burst is one person's anyway, and the id would only end up in the telemetry events.
 */
export const setAskPrincipal = (next: Principal | null, tenant: TenantId = null): void => {
  principal = next;
  tenantId = tenant;
};

/** Thrown when the body the panel built does not validate — a programming error, not a user one. */
export class AskClientError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(
      `ask-client: ${status} ${
        (body as Partial<ValidationErrorBody>)?.error?.issues
          ?.map((issue) => `${issue.path || '(root)'}: ${issue.message}`)
          .join('; ') ?? 'request rejected'
      }`,
    );
    this.name = 'AskClientError';
  }
}

/** Same signature as `ask-api.ts`'s `searchAssistant` — computed here, no request. */
export const searchAssistant = async (args: AssistantSearchRequest): Promise<AssistantSearchResponse> => {
  const result = await handleSearch(args, principal, tenantId);
  if (result.status !== 200) throw new AskClientError(result.status, result.body);
  return result.body as AssistantSearchResponse;
};

/** Same signature as `ask-api.ts`'s `sendAssistantFeedback`. */
export const sendAssistantFeedback = async (args: AssistantFeedback): Promise<AssistantFeedbackResponse> => {
  const result = await handleFeedback(args, principal, tenantId);
  if (result.status !== 202) throw new AskClientError(result.status, result.body);
  return result.body as AssistantFeedbackResponse;
};
